import { useMemo, useState } from 'react';
import { Plus, TriangleAlert, UserPlus, X } from 'lucide-react';
import type { AdminTeam, PersonRef } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/portal/modal';
import { PageTitle } from '@/components/portal/page-title';
import { Pill } from '@/components/portal/pill';
import { ProductTag } from '@/components/portal/product-tag';
import { SearchField } from '@/components/portal/search-field';
import { SectionCard } from '@/components/portal/section-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UserInviteForm } from '@/features/admin/user-invite-form';
import { Field, inputClass } from '@/features/requests/request-form';
import { useAdminProducts, useAdminTeams, useAdminUsers, useSaveTeam } from '@/hooks/use-admin';
import { ApiValidationError } from '@/lib/api';
import { fullName } from '@/lib/format';
import { cn } from '@/lib/utils';

interface TeamDraft {
  id: string | null;
  name: string;
  membersSeeTeamRequests: boolean;
  productIds: string[];
  members: (PersonRef & { isReferent: boolean })[];
}

const emptyDraft = (): TeamDraft => ({ id: null, name: '', membersSeeTeamRequests: false, productIds: [], members: [] });

function TeamForm({ draft, setDraft, onDone }: { draft: TeamDraft; setDraft: (d: TeamDraft) => void; onDone: () => void }) {
  const save = useSaveTeam();
  const products = useAdminProducts();
  const [q, setQ] = useState('');
  const people = useAdminUsers({ q, role: '', teamId: '', page: 1 });
  const candidates = q.trim() ? (people.data?.items ?? []).filter((u) => !draft.members.some((m) => m.id === u.id)).slice(0, 6) : [];
  const errors = save.error instanceof ApiValidationError ? Object.fromEntries(save.error.issues.map((i) => [String(i.path[0]), i.message])) : {};
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(
          {
            id: draft.id,
            input: { name: draft.name, membersSeeTeamRequests: draft.membersSeeTeamRequests, productIds: draft.productIds, memberIds: draft.members.map((m) => m.id) },
          },
          { onSuccess: onDone },
        );
      }}
    >
      <Field id="nom" label="Nom de l'équipe" required error={errors.name}>
        <input id="nom" type="text" required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputClass} />
      </Field>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="membres" className="text-sm font-bold">
          Membres
        </label>
        <input id="membres" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ajouter une personne déjà connectée au portail" className={inputClass} />
        {candidates.length > 0 && (
          <ul className="m-0 flex list-none flex-col rounded-control border border-border p-0">
            {candidates.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className="w-full cursor-pointer border-0 bg-card px-3 py-2 text-left font-[inherit] text-[13px] hover:bg-accent"
                  onClick={() => {
                    setDraft({ ...draft, members: [...draft.members, { id: u.id, firstName: u.firstName, lastName: u.lastName, isReferent: u.displayedRole === 'referent' }] });
                    setQ('');
                  }}
                >
                  <strong>{fullName(u)}</strong> <span className="text-muted-foreground">{u.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-1.5">
          {draft.members.map((m) => (
            <span key={m.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-subtle py-1 pl-3 pr-1.5 text-[13px]">
              {fullName(m)}
              {m.isReferent && <Pill className="bg-role-referent px-2 py-0 text-[11px] text-role-referent-foreground">Référent</Pill>}
              <button
                type="button"
                aria-label={`Retirer ${fullName(m)}`}
                onClick={() => setDraft({ ...draft, members: draft.members.filter((x) => x.id !== m.id) })}
                className="flex cursor-pointer border-0 bg-transparent p-0.5 text-muted-foreground"
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">Le rôle de référent s'attribue dans Entra ID, pas ici.</span>
      </div>
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-2 p-0 text-sm font-bold">Produits accessibles</legend>
        {(products.data?.items ?? []).map((p) => (
          <label key={p.id} className="flex items-center gap-2.5 text-[13px]">
            <input
              type="checkbox"
              checked={draft.productIds.includes(p.id)}
              onChange={(e) => setDraft({ ...draft, productIds: e.target.checked ? [...draft.productIds, p.id] : draft.productIds.filter((id) => id !== p.id) })}
            />
            {p.name}
            {!p.isActive && <span className="text-muted-foreground">(inactif)</span>}
          </label>
        ))}
      </fieldset>
      {/* Not in mock-up 10: the team option of the permission matrix needs a place to be set. */}
      <label className="flex items-center gap-2.5 text-[13px]">
        <input type="checkbox" checked={draft.membersSeeTeamRequests} onChange={(e) => setDraft({ ...draft, membersSeeTeamRequests: e.target.checked })} />
        Les demandeurs voient les demandes de leur équipe
      </label>
      {save.error && !(save.error instanceof ApiValidationError) && (
        <p role="alert" className="m-0 text-sm font-bold text-destructive">
          {save.error.message}
        </p>
      )}
      <div className="flex justify-between gap-2.5 border-t border-border pt-4">
        <Button type="button" variant="secondary" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" disabled={save.isPending || !draft.name.trim()}>
          {draft.id ? 'Enregistrer' : "Créer l'équipe"}
        </Button>
      </div>
    </form>
  );
}

const PAGE_SIZE = 10;

/**
 * 10 · Équipes. Referents are deduced from the Entra role and displayed; they are never designated here.
 * Creation and edition open in a pop-in (2026-10-22), as does « Créer un nouvel utilisateur » (ADR-014).
 */
export function TeamsPage() {
  const teams = useAdminTeams();
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState<TeamDraft | null>(null);
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState<{ email: string; emailSent: boolean } | null>(null);
  const [showAll, setShowAll] = useState(false);
  const filtered = useMemo(() => (teams.data ?? []).filter((t) => t.name.toLowerCase().includes(q.trim().toLowerCase())), [teams.data, q]);
  const shown = showAll || q ? filtered : filtered.slice(0, PAGE_SIZE);
  const edit = (t: AdminTeam) =>
    setDraft({ id: t.id, name: t.name, membersSeeTeamRequests: t.membersSeeTeamRequests, productIds: t.products.map((p) => p.id), members: t.members });

  return (
    <>
      <PageTitle title="Équipes métier" subtitle="Une équipe regroupe des collaborateurs et les produits sur lesquels ils peuvent faire des demandes. Les membres sont rattachés à la main.">
        <Button type="button" variant="secondary" onClick={() => setInviting(true)}>
          <UserPlus aria-hidden="true" className="size-4" /> Créer un nouvel utilisateur
        </Button>
        <Button type="button" onClick={() => setDraft(emptyDraft())}>
          <Plus aria-hidden="true" className="size-4" /> Créer une nouvelle équipe
        </Button>
      </PageTitle>
      {invited && (
        <p role="status" className="m-0 rounded-control border border-border bg-card px-4 py-3 text-[13px]">
          Compte créé pour <strong>{invited.email}</strong>.{' '}
          {invited.emailSent ? "L'invitation est partie par email." : "L'email d'invitation n'a pas pu partir : prévenez la personne vous-même."} Il sera relié à sa première connexion.
        </p>
      )}
      <SectionCard
        title={`${teams.data?.length ?? 0} équipes`}
        toolbar={
          <>
            <span className="text-xs text-muted-foreground">
              Référents : membres portant le rôle Entra <code className="font-mono">product_portal.referent</code>, vus à leur dernière connexion
            </span>
            <SearchField label="Rechercher une équipe" placeholder="Rechercher une équipe" value={q} onChange={setQ} className="ml-auto max-w-72" />
          </>
        }
        footer={
          !showAll && !q && filtered.length > PAGE_SIZE ? (
            <div className="border-t border-border px-5 py-3 text-[13px] text-muted-foreground">
              {PAGE_SIZE} sur {filtered.length} ·{' '}
              <button type="button" onClick={() => setShowAll(true)} className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-bold text-primary">
                Afficher tout
              </button>
            </div>
          ) : undefined
        }
      >
        <Table minWidth={620}>
          <TableHeader>
            <TableRow>
              <TableHead>Équipe</TableHead>
              <TableHead>Référent(s)</TableHead>
              <TableHead>Membres</TableHead>
              <TableHead>Produits</TableHead>
              <TableHead>
                <span className="sr-only">Action</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((t) => (
              <TableRow key={t.id} className={cn(draft?.id === t.id && 'bg-accent')}>
                <TableCell className="font-bold">{t.name}</TableCell>
                <TableCell>
                  {t.referents.length > 0 ? (
                    t.referents.map(fullName).join(', ')
                  ) : (
                    <span className="inline-flex items-center gap-1.5 font-bold text-warning-foreground">
                      <TriangleAlert aria-hidden="true" className="size-4" /> Aucun référent
                    </span>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">{t.members.length}</TableCell>
                <TableCell>
                  <span className="flex flex-wrap gap-1">
                    {t.products.map((p) => (
                      <ProductTag key={p.id} name={p.name} />
                    ))}
                  </span>
                </TableCell>
                <TableCell>
                  <button type="button" onClick={() => edit(t)} className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] font-bold text-primary">
                    Modifier
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>
      {draft && (
        <Modal titleId="equipe-titre" title={draft.id ? `Modifier · ${draft.name}` : 'Nouvelle équipe'} onClose={() => setDraft(null)}>
          <TeamForm key={draft.id ?? 'new'} draft={draft} setDraft={setDraft} onDone={() => setDraft(null)} />
        </Modal>
      )}
      {inviting && (
        <Modal titleId="invite-titre" title="Nouvel utilisateur" onClose={() => setInviting(false)}>
          <UserInviteForm
            onDone={(result) => {
              setInviting(false);
              setInvited(result);
            }}
          />
        </Modal>
      )}
    </>
  );
}
